// resources/js/calculator/api.js
// The /calculate SEAM (CONTEXT D-03), flipped in Phase 5 from the Phase-3 client mock to the
// real server endpoint. The qualification engine runs in PHP so offers.json's NDA financier
// names never reach the browser (ResultBuilder is the single sanitization chokepoint); the
// response here already carries ONLY whitelisted display fields (ui_label + ui_hint + amounts
// + counts). No change to state.js or the Blade calculator wrapper — the exported signature
// calculate(input) is identical to the mock it replaces.
//
// LOCKED contract (CONTEXT D-02):
//   Request  { avg_monthly_turnover_pln:int, tenure_band: 0_3|3_6|6_12|12_plus,
//              industry_vertical: restauracja|sklep|hotel|uslugi }
//   Response { result_type: standard|high_eligibility|no_match, eligible_count, displayed_count,
//              high_eligibility_badge (count>=6), result_range_min_pln, result_range_max_pln,
//              options:[ {ui_label, amount_max_pln, ui_hint} ] }
//
// A non-OK response (400 bad enum / 429 rate-limited) throws, which state.js.submit() already
// routes to the CALC-08 error + "spróbuj ponownie" retry path (CONTEXT D-02 / D-04).

/**
 * Qualification call against the live WP REST endpoint. Resolves to the sanitized
 * response shape (no partner strings); throws on any non-OK HTTP status.
 *
 * @param {{avg_monthly_turnover_pln:number, tenure_band:string, industry_vertical:string}} input
 * @returns {Promise<Object>} sanitized result (no partner strings)
 */
export async function calculate(input) {
  const r = await fetch('/wp-json/obrotomat/v1/calculate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!r.ok) throw new Error('calculate failed: ' + r.status)
  return r.json()
}

/**
 * Lead submit against the live WP REST endpoint (06-RESEARCH Pattern 1). Mirrors calculate()'s
 * fetch + throw-on-not-ok shape. A non-OK status (400 bad payload / 429 rate-limited / 5xx / network)
 * throws — state.js.submitLead() routes that to the D-07 "stay on the form + Spróbuj ponownie" path.
 *
 * The 200 body's `track` field gates the GA4 conversion push: a bot/duplicate is served an
 * identical fake-success 200 with `track: false` (D-09), so no `lead_phone_submitted` fires.
 * Offer ids NEVER appear in this response — the client only ever sends a tile INDEX; the server
 * maps index → offer_id on its deterministic re-run (sanitization holds, D-03).
 *
 * @param {{phone:string, consent_call:boolean, consent_sms:boolean, avg_monthly_turnover_pln:number,
 *          tenure_band:string, industry_vertical:string, selected_option_index:number|null,
 *          elapsed_ms:number, website:string, page_url:string}} input
 * @returns {Promise<{status:string, within_working_hours:boolean, delivered:boolean, track:boolean}>}
 */
export async function submitLead(input) {
  const r = await fetch('/wp-json/obrotomat/v1/lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!r.ok) throw new Error('lead failed: ' + r.status) // 400/429/5xx → state.js D-07 stay-on-form path
  return r.json() // { status, within_working_hours, delivered, track }
}
